<template>
  <div ref="navigationDrawer">
    <v-navigation-drawer
      id="app-drawer"
      :label="t(l.label_burger_menu_button)"
      v-model="isBurgerMenuOpen"
      class="burger-navigation"
      floating
      temporary
      :aria-hidden="!isBurgerMenuOpen"
      :tabindex="isBurgerMenuOpen ? 0 : -1"
      :inert="!isBurgerMenuOpen"
      @update:modelValue="toggleBurgerMenu"
      @keydown.esc="closeBurgerMenu"
    >
      <v-list class="burger-menu--list" tag="ul" role="list">
        <li>
          <v-list-item :title="t(l.menu.my_page)" :to="{ name: 'myPage' }" ripple class="burger-menu--item gc-4">
            <template #prepend>
              <v-icon icon="fa:fat fa-circle-user" size="20" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item
            :title="t(l.menu.manage_users)"
            :to="{ name: 'ControlPanel' }"
            ripple
            class="burger-menu--item gc-4"
          >
            <template #prepend>
              <v-icon icon="fa:fat fa-user-group" size="20" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item class="burger-menu--item gc-4" :href="homeUrl" target="_blank" ripple>
            <template #prepend>
              <v-icon icon="fa:fat fa-grid" size="20" />
            </template>
            <v-list-item-title class="list-item-title-left-aligned">
              {{ t(l.menu.dashboard) }}
            </v-list-item-title>
          </v-list-item>
        </li>
        <li>
          <v-list-item class="gc-4 burger-menu--item" :title="t(l.menu.settings)" :href="settingsUrl" target="_blank">
            <template #prepend>
              <v-icon icon="fa:fat fa-gear" size="20" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item
            v-if="teacherGuideUrl && showTeacherGuide && userIsNotStudent"
            class="burger-menu--item gc-4"
            :title="t(l.menu.teacher_guide)"
            :href="teacherGuideUrl"
            target="_blank"
            ripple
          >
            <template #prepend>
              <v-icon icon="fa:fat fa-book-open-reader" size="20" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item
            v-if="nativeLanguageTeacherUrl && showNativeLanguageTeacher && userIsNotStudent"
            class="burger-menu--item gc-4"
            :title="t(l.menu.native_language_teacher)"
            :href="nativeLanguageTeacherUrl"
            target="_blank"
            ripple
          >
            <template #prepend>
              <v-icon icon="fa:fat fa-chalkboard-user" size="20" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item v-if="supportUrl" class="burger-menu--item gc-4" :href="supportUrl" target="_blank" ripple>
            <template #prepend>
              <v-icon icon="fa:fat fa-circle-question" size="20" />
            </template>
            <v-list-item-title class="list-item-title-left-aligned">
              {{ t(l.menu.support) }}
            </v-list-item-title>
          </v-list-item>
        </li>
        <li>
          <v-list-item
            v-if="feedbackFormUrl && userIsNotStudent"
            class="burger-menu--item gc-4"
            :href="feedbackFormUrl"
            target="_blank"
            ripple
          >
            <template #prepend>
              <v-icon icon="fa:fat fa-message-exclamation" size="20" />
            </template>
            <v-list-item-title class="list-item-title-left-aligned">
              {{ t(l.menu.feedback_form) }}
            </v-list-item-title>
          </v-list-item>
        </li>
        <li>
          <v-list-item v-if="showDarkModeToggle" class="burger-menu--item gc-4">
            <template #prepend>
              <v-icon icon="fa:fat fa-moon" size="20" />
            </template>
            <v-list-item-title>{{ t(l.menu.dark_mode) }}</v-list-item-title>
            <template #append>
              <v-switch
                v-model="darkMode"
                hide-details
                inset
                @update:modelValue="toggleDarkMode"
                :aria-label="t(l.menu.dark_mode_toggle)"
              />
            </template>
          </v-list-item>
        </li>
      </v-list>

      <template #append>
        <div class="pa-4">
          <v-btn class="text-none button-log-out-user" block color="secondary" @click="onLogout">
            {{ t(l.button_log_out_user) }}
          </v-btn>
          <div class="version-number-text">
            {{ t(l.label_version_number) + ': ' + appVersion }}
          </div>
        </div>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script setup lang="ts">
import { useUserContext } from '@ilteducation/ilt-auth';
import { computed, nextTick, ref, watch } from 'vue';
import { useLocale } from '@/plugins/translation';
import useGuiStore from '@/store/gui';
import { storeToRefs } from 'pinia';
import useLogout from '@/foundations/helpers/use-logout';
import { useTheme } from 'vuetify';
import config from '../../config/ConfigProvider';
// @ts-ignore - this is the recommended way to import it - https://www.npmjs.com/package/@vueuse/integrations
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import useAppVersion from '@/composables/use-app-version';

const { appVersion } = useAppVersion();
const userContext = useUserContext();
const showDarkModeToggle = computed(() => userContext.hasScope('ilt.content.write'));
const theme = useTheme();
const darkMode = ref(theme.global.current.value.dark);

const toggleDarkMode = () => {
  theme.global.name.value = darkMode.value ? 'baseDark' : 'light';
};

const homeUrl = config.value('homeUrl');
const settingsUrl = config.value('settingsUrl');
const supportUrl = config.value('supportUrl');
const feedbackFormUrl = config.value('feedbackFormUrl');
const showTeacherGuide = config.value('showTeacherGuide');
const teacherGuideUrl = config.value('teacherGuideUrl');
const nativeLanguageTeacherUrl = config.value('nativeLanguageTeacherUrl');
const showNativeLanguageTeacher = config.value('showNativeLanguageTeacher');

const guiStore = useGuiStore();
const { isBurgerMenuOpen } = storeToRefs(guiStore);
const { toggleBurgerMenu, closeBurgerMenu } = guiStore;
const logout = useLogout();

const userIsNotStudent = computed(() => !userContext.hasRole('student'));
const { t, l } = useLocale();

const onLogout = async () => {
  await logout();
};

const navigationDrawer = ref(null);
const { activate: activateDrawerFocus, deactivate: deactivateDrawerFocus } = useFocusTrap(navigationDrawer, {
  allowOutsideClick: true,
});
const moveFocusToDrawer = () => {
  if (isBurgerMenuOpen.value) {
    nextTick(() => {
      if (navigationDrawer.value) {
        activateDrawerFocus();
      } else {
        // Try again until the element is available
        moveFocusToDrawer();
      }
    });
  }
};
watch(isBurgerMenuOpen, () => {
  if (isBurgerMenuOpen.value) {
    moveFocusToDrawer();
  } else {
    nextTick(() => {
      deactivateDrawerFocus();
    });
  }
});
</script>

<style scoped lang="scss">
.burger-menu--list > .burger-menu--item {
  grid-template-columns: 24px 1fr auto;
}
:deep(.v-list-item__prepend) {
  width: 25px;
}
:deep(.v-list-item__prepend > .v-icon) {
  opacity: 1 !important;
}

.burger-navigation {
  display: flex;
  flex-direction: column;
  z-index: 20;
}

.v-list-item {
  transition: all 0.3s ease;

  .v-list-item__title,
  .v-icon {
    color: rgb(var(--v-theme-on-surface)) !important;
  }

  svg {
    path {
      transition: all 0.3s ease;
    }
  }

  &:before,
  &:after {
    display: none;
  }

  &:hover {
    .v-list-item__title,
    .v-icon {
      color: rgba(var(--v-theme-brand));
    }

    svg {
      path {
        fill: rgb(var(--v-theme-brand)) !important;
      }
    }
  }

  &__icon {
    margin-right: 16px !important;
  }
}

.list-item-title-left-aligned {
  text-align: start;
}

.v-navigation-drawer__content > .v-list {
  padding-top: 0;
}

.v-toolbar__content button:focus {
  outline: 1px solid #5f30c5;
}

.button-log-out-user {
  letter-spacing: normal;
  font-size: 0.875rem;
  border-radius: 4px;
}

.version-number-text {
  font-size: 0.75rem;
  margin-top: 12px;
  margin-bottom: 8px;
  text-align: center;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.7;
}

.inner-list {
  .v-list-group__items {
    .v-list-item {
      padding-left: 42px !important;
      cursor: pointer;
    }
  }
}
</style>
